import { type FC, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { MultiSelect, type Option } from 'react-multi-select-component';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import { AccessCodeDurationUnit, AccessCodeKind } from '../../../modules/enums/types';
import { useNotifications } from '../../../modules/hooks';
import type { LockRecord } from '../../../modules/interfaces';
import { useLocks } from '../../Dashboards/LockManagement/useLocks';
import { useAccessCodes } from '../../Dashboards/TemporaryAccessCodes/useAccessCodes';

interface AccessCodeModalProps {
  show: boolean;
  handleClose: () => void;
  onSaved?: () => void;
}

const DURATION_UNIT_OPTIONS: { value: AccessCodeDurationUnit; label: string }[] = [
  { value: AccessCodeDurationUnit.MINUTES, label: 'Minutes' },
  { value: AccessCodeDurationUnit.HOURS, label: 'Hours' },
  { value: AccessCodeDurationUnit.DAYS, label: 'Days' },
  { value: AccessCodeDurationUnit.WEEKS, label: 'Weeks' },
];

/** Staff-facing create modal — always creates a `temporary` access code. */
export const AccessCodeModal: FC<AccessCodeModalProps> = ({
  show,
  handleClose,
  onSaved,
}) => {
  const { setSimpleToasts } = useNotifications()!;
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const accessCodes = useAccessCodes();
  const locksService = useLocks();

  // The provider is the property's configured door-access provider, sent under the hood.
  const provider = selectedProperty?.property?.doorAccessProvider;

  const [name, setName] = useState('');
  const [selectedLocks, setSelectedLocks] = useState<Option[]>([]);
  const [locks, setLocks] = useState<LockRecord[]>([]);
  const [duration, setDuration] = useState('1');
  const [durationUnit, setDurationUnit] = useState<AccessCodeDurationUnit>(
    AccessCodeDurationUnit.DAYS,
  );
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);

  const lockOptions = useMemo<Option[]>(
    () => locks.map((lock) => ({ label: lock.name, value: lock.uuid })),
    [locks],
  );

  useEffect(() => {
    if (!show) return;
    setName('');
    setSelectedLocks([]);
    setDuration('1');
    setDurationUnit(AccessCodeDurationUnit.DAYS);
    setGuestFirstName('');
    setGuestLastName('');
    setGuestEmail('');
    setPhoneNumber('');
    locksService
      .list()
      .then(setLocks)
      .catch(() =>
        setSimpleToasts({ type: 'danger', message: 'Failed to load locks', show: true }),
      );
  }, [show, locksService, setSimpleToasts]);

  const handleSave = async () => {
    if (!provider) {
      setSimpleToasts({
        type: 'danger',
        message: 'This property has no door access provider configured',
        show: true,
      });
      return;
    }
    if (selectedLocks.length === 0) {
      setSimpleToasts({
        type: 'danger',
        message: 'Please select at least one lock',
        show: true,
      });
      return;
    }
    const durationValue = Number(duration);
    if (!durationValue || durationValue <= 0) {
      setSimpleToasts({
        type: 'danger',
        message: 'Please enter a valid duration',
        show: true,
      });
      return;
    }
    setSaving(true);
    try {
      await accessCodes.create({
        name: name.trim() || undefined,
        kind: AccessCodeKind.TEMPORARY,
        provider,
        lockUuids: selectedLocks.map((option) => option.value),
        duration: durationValue,
        durationUnit,
        guestFirstName: guestFirstName.trim() || undefined,
        guestLastName: guestLastName.trim() || undefined,
        guestEmail: guestEmail.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
      });
      setSimpleToasts({ type: 'success', message: 'Access code created', show: true });
      onSaved?.();
      handleClose();
    } catch {
      setSimpleToasts({
        type: 'danger',
        message: 'Failed to create access code',
        show: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>New access code</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Label (optional)</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. Front desk staff"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Provider</Form.Label>
          {/* Read-only for now: inherited from the selected property's
              door-access provider (per-property providers come later). */}
          <Form.Select value={provider ?? ''} disabled>
            {provider ? (
              <option value={provider}>{provider}</option>
            ) : (
              <option value="">No provider configured</option>
            )}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Locks</Form.Label>
          <MultiSelect
            options={lockOptions}
            value={selectedLocks}
            onChange={setSelectedLocks}
            labelledBy="Select locks"
          />
        </Form.Group>

        <Row>
          <Col xs={6}>
            <Form.Group className="mb-3">
              <Form.Label>Duration</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={duration}
                onChange={(event) => setDuration(event.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group className="mb-3">
              <Form.Label>Unit</Form.Label>
              <Form.Select
                value={durationUnit}
                onChange={(event) =>
                  setDurationUnit(event.target.value as AccessCodeDurationUnit)
                }
              >
                {DURATION_UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col xs={6}>
            <Form.Group className="mb-3">
              <Form.Label>Guest first name (optional)</Form.Label>
              <Form.Control
                type="text"
                value={guestFirstName}
                onChange={(event) => setGuestFirstName(event.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group className="mb-3">
              <Form.Label>Guest last name (optional)</Form.Label>
              <Form.Control
                type="text"
                value={guestLastName}
                onChange={(event) => setGuestLastName(event.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col xs={6}>
            <Form.Group className="mb-3">
              <Form.Label>Guest email (optional)</Form.Label>
              <Form.Control
                type="email"
                value={guestEmail}
                onChange={(event) => setGuestEmail(event.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group className="mb-3">
              <Form.Label>Phone number (optional)</Form.Label>
              <Form.Control
                type="text"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button className="cancel-button" onClick={handleClose}>
          Cancel
        </Button>
        <Button className="save-button" onClick={handleSave} disabled={saving}>
          {saving ? 'Creating…' : 'Create code'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
